import React, { FC, useEffect, useRef, useState } from "react";
import { addStyles, EditableMathField, StaticMathField } from "react-mathquill";
import { Button, Col, Modal, Row, Tooltip } from "antd";
import { keyMap } from "./latexRenderer";
import { MathComponent } from "mathjax-react";
import CKEditor from "ckeditor4-react";

import "./ckEditorComponent.scss";

import Undo from "./../../../assets/icons/math-editor/undo.png";
import Redo from "./../../../assets/icons/math-editor/redo.png";

addStyles();

interface CKEditorComponentProps {
  title?: string;
  value: any;
  onChange?: (data: any) => void;
  disabled?: boolean;
}
const CKEditorComponent: FC<CKEditorComponentProps> = ({
  value,
  onChange,
  disabled = false,
  title,
}) => {
  const editorConfig = {
    versionCheck: false,
    extraPlugins: "mathjax",
    mathJaxLib:
      "//cdnjs.cloudflare.com/ajax/libs/mathjax/2.7.4/MathJax.js?config=TeX-AMS_HTML",
    mathJaxClass: "equation",
    toolbar: [
      ["Format"],
      ["Bold", "Italic", "Link"],
      ["NumberedList", "BulletedList"],
      ["Undo", "Redo"],
      ["Mathjax"],
    ],
    removePlugins: "elementspath",
    height: 100,
  };
  const [latex, setLatex] = useState("");
  const [editorData, setEditorData] = useState<string>(value);
  const editorRef = useRef<any>(null);
  const mathFieldRef = useRef<any>(null);
  const [openResponsive, setOpenResponsive] = useState(false);
  const [undoStack, setUndoStack] = useState<string[]>([]);
  const [redoStack, setRedoStack] = useState<string[]>([]);
  const [undoRedoClicked, setUndoRedoClicked] = useState(false);

  const handleChange = (mathExpression) => {
    if (!undoRedoClicked) {
      setUndoStack([...undoStack, latex]);
    }

    setLatex(mathExpression);
  };

  const handleUndo = () => {
    if (undoStack.length > 0) {
      const prevState = undoStack[undoStack.length - 1];
      setRedoStack([latex, ...redoStack]);
      setUndoStack((prevState) => prevState.slice(0, -1));
      setLatex(prevState);
    }

    setUndoRedoClicked(true);
  };

  const handleRedo = () => {
    if (redoStack.length > 0) {
      const nextState = redoStack[0];
      setUndoStack([...undoStack, latex]);
      setLatex(nextState);
      setRedoStack(redoStack.slice(1));
    }
    setUndoRedoClicked(true);
  };

  const insertMath = () => {
    const latexMarkup = `<span class="equation" data=${latex}>\\(${latex}\\)</span>`;
    editorRef.current.editor.insertHtml(latexMarkup);
    setEditorData(() => {
      return editorRef.current.editor.getData();
    });
    editorRef.current.editor.fire("change");
    setOpenResponsive(false);
    setLatex("");
  };

  const insertLatex = (symbol: string) => {
    if (mathFieldRef.current) {
      const mathField = mathFieldRef.current.mathField;
      if (mathField) {
        mathField.write(symbol);
        setLatex(mathField.latex());
      }
    }
  };

  useEffect(() => {
    const handleMutations = (mutationsList) => {
      if (editorRef && editorRef.current && editorRef.current.editor) {
        let document1 =
          editorRef.current.editor.container.$.querySelector(
            ".cke_wysiwyg_frame"
          ).contentWindow.document;
        let equations = document1.querySelectorAll(
          ".cke_widget_wrapper.cke_widget_inline.cke_widget_mathjax.cke_widget_wrapper_equation"
        );

        equations.forEach((equation) => {
          equation.classList.add("disable-events");
          equation.addEventListener("dblclick", function (e) {
            const firstChild = equation.firstElementChild;
            const encodedString = firstChild.getAttribute(
              "data-cke-widget-data"
            );

            const decodedString = decodeURIComponent(encodedString);
            const jsonData = JSON.parse(decodedString);
            let latexExpression = jsonData.math;

            latexExpression = latexExpression.replace(/^\\\(/, "");
            latexExpression = latexExpression.replace(/\\\)$/, "");

            setLatex(latexExpression);
            e.stopPropagation();
            e.preventDefault();
            setOpenResponsive(true);
          });
        });
      }
    };
    const observer = new MutationObserver(handleMutations);
    const config = { childList: true, subtree: true };
    observer.observe(document.body, config);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const handleMutations = () => {
      if (editorRef && editorRef.current && editorRef.current.editor) {
        let mathjaxCmd = editorRef.current.editor.commands["mathjax"];
        mathjaxCmd.on("exec", (event) => {
          setOpenResponsive(true);

          return false;
        });
      }
    };
    const observer = new MutationObserver(handleMutations);
    const config = { childList: true, subtree: true };
    observer.observe(document.body, config);

    return () => {
      observer.disconnect();
    };
  }, []);

  const renderMathQuill = () => {
    return (
      <div className="editor-container">
        <div className="button-container">
          {keyMap.map(
            (t, i) =>
              t.tab === "Default" && (
                <Row justify="space-around" align="middle">
                  {t.keys.map((btn, index) => (
                    <Col span={4}>
                      <Tooltip title={btn.label} color={"blue"} key={index}>
                        <Button
                          onClick={() => insertLatex(btn.mathCode)}
                          className="math-button"
                        >
                          {btn.useJax ? (
                            <MathComponent
                              tex={btn.charCode ? btn.charCode : btn.mathCode}
                            />
                          ) : (
                            <StaticMathField>
                              {btn.charCode ? btn.charCode : btn.mathCode}
                            </StaticMathField>
                          )}
                        </Button>
                      </Tooltip>
                    </Col>
                  ))}
                </Row>
              )
          )}
        </div>
        <div>
          <EditableMathField
            latex={latex}
            onChange={(mathField) => {
              handleChange(mathField.latex());
              setUndoRedoClicked(false);
            }}
            ref={mathFieldRef}
          />
        </div>

        <Row justify="start" align="middle" gutter={16}>
          <Col span={2} className="undoRedo">
            <img
              src={Undo}
              height={20}
              width={20}
              onClick={handleUndo}
              alt=""
            />
          </Col>
          <Col span={2} className="undoRedo">
            <img
              src={Redo}
              height={20}
              width={20}
              onClick={handleRedo}
              alt=""
            />
          </Col>
        </Row>
        <Row justify="end" align="middle" gutter={16}>
          <Col span={6}>
            <Button onClick={() => setOpenResponsive(false)}>Cancel</Button>
          </Col>
          <Col span={6}>
            <Button onClick={insertMath} type="primary">
              Insert Math
            </Button>
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <div className="ck-editor-component__container">
      <p className="ck-editor__title">{title}</p>
      <Modal
        title="Type Math Equations"
        centered
        className="editor-container-modal"
        open={openResponsive}
        onOk={() => setOpenResponsive(false)}
        onCancel={() => setOpenResponsive(false)}
      >
        <div>{renderMathQuill()}</div>
      </Modal>
      <CKEditor
        initData={value}
        ref={editorRef}
        config={editorConfig}
        data={editorData}
        onChange={(event) => {
          const data = event.editor.getData();
          onChange && onChange(data);
          setEditorData(data);
        }}
      />
    </div>
  );
};

export default CKEditorComponent;
